import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  ApiError,
  api,
  authHeadersOnly,
  LAST_WORLD_STORAGE_KEY,
  type AvailableExit,
  type HistoryEntry,
  type Intent,
  type PresentNPC,
  type QuickAction,
  type TakeableItem,
  type SessionByWorldResponse,
  type SessionResponse,
  type TurnDoneMeta,
} from "./api";
import { apiBase } from "./api";
import { fadeAudioIn, fadeAudioOut, playSfx, SILENT_MP3 } from "./audio";
import { b64ToBytes, parseSseEvent } from "./sse";

export type TurnEntryKind = "turn" | "narration" | "error" | "system";

export interface TurnEntry {
  id: number;
  kind: TurnEntryKind;
  action?: string;
  narration?: string;
  cached?: boolean;
  streaming?: boolean;
  text?: string; // for error / system
}

export interface GameState {
  busy: boolean;
  // Issue #588: true while restart() is awaiting the bootstrap
  // narration (~10s round-trip). Header uses it to disable the reset
  // button and show a spinner so the player knows the click registered.
  restarting: boolean;
  worldTitle: string;
  locationName: string;
  turn: number;
  sessionId: string | null;
  status: string;
  ambientImageUrl: string | null;
  ambientAudioUrl: string | null;
  ambientMuted: boolean;
  entries: TurnEntry[];
  // Issue #444: exits at the player's current location, for the
  // quick-actions strip. Empty when the player has no exits (or boot
  // hasn't completed yet).
  availableExits: AvailableExit[];
  // Issue #493: NPCs at the player's current location, for the do/say
  // toggle's optional "say to" dropdown. Empty when no NPCs are
  // present or boot hasn't completed yet.
  presentNpcs: PresentNPC[];
  // Issue #545: takeable items at the player's current location, for
  // the "Take" quick-action chip's drop-up popover. Empty when no
  // takeable items are present or boot hasn't completed yet.
  takeableEntities: TakeableItem[];
}

export interface GameApi extends GameState {
  submitAction: (
    action: string,
    opts?: {
      quickAction?: QuickAction;
      // Issue #493: do/say intent toggle. When omitted, the server
      // runs the legacy router path (intent: null on the wire).
      intent?: Intent;
      targetEntityId?: string;
    },
  ) => Promise<void>;
  restart: () => Promise<void>;
  toggleAmbient: () => void;
  unlockAudio: () => void;
  registerAmbientEl: (el: HTMLAudioElement | null) => void;
  registerSfxEl: (el: HTMLAudioElement | null) => void;
  registerTurnAudioEl: (el: HTMLAudioElement | null) => void;
}

const GameContext = createContext<GameApi | null>(null);

export function useGame(): GameApi {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used inside <GameProvider>");
  return ctx;
}

let _entryId = 0;
const nextId = () => ++_entryId;

// Issue #541: narration occasionally arrives with literal leading/trailing
// "\n\n" (model-emitted or stream-boundary artefacts). The .narration CSS
// class uses `white-space: pre-wrap`, so those boundary newlines render as
// an ugly blank-line gap between the action chip and the prose. Strip
// leading/trailing whitespace only — internal "\n\n" paragraph breaks
// must be preserved.
function stripBoundaryWhitespace(s: string | undefined): string {
  if (!s) return s ?? "";
  return s.replace(/^\s+|\s+$/g, "");
}


// Issue #535: resolve the world slug for the current /play session.
// URL ``?world=`` wins (so the picker's navigate-with-query call is
// authoritative for the page load), then localStorage as the
// remembered-last-pick fallback, and finally null which forces the
// picker. Exported for tests.
export function resolveWorldSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("world");
    if (fromUrl) return fromUrl;
    const fromStorage = localStorage.getItem(LAST_WORLD_STORAGE_KEY);
    return fromStorage && fromStorage.length > 0 ? fromStorage : null;
  } catch {
    return null;
  }
}

export function GameProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Issue #588: separate from `busy` (which tracks per-turn submissions)
  // because the restart endpoint is its own ~10s round-trip and the
  // header reset button needs its own loading affordance.
  const [restarting, setRestarting] = useState(false);
  const [worldTitle, setWorldTitle] = useState("Lucid");
  const [locationName, setLocationName] = useState("");
  const [turn, setTurn] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [ambientImageUrl, setAmbientImageUrl] = useState<string | null>(null);
  const [ambientAudioUrl, setAmbientAudioUrl] = useState<string | null>(null);
  const [ambientMuted, setAmbientMuted] = useState<boolean>(
    typeof window !== "undefined" && localStorage.getItem("lucid_ambient_muted") === "true",
  );
  const [entries, setEntries] = useState<TurnEntry[]>([]);
  const [availableExits, setAvailableExits] = useState<AvailableExit[]>([]);
  const [presentNpcs, setPresentNpcs] = useState<PresentNPC[]>([]);
  const [takeableEntities, setTakeableEntities] = useState<TakeableItem[]>([]);
  // Issue #535: world this provider's session belongs to. Resolved
  // once at mount from ``?world=`` / localStorage; turn submissions
  // include it in their body so the backend can route to the right
  // world.
  const worldNameRef = useRef<string | null>(resolveWorldSlug());

  const ambientElRef = useRef<HTMLAudioElement | null>(null);
  const sfxElRef = useRef<HTMLAudioElement | null>(null);
  const turnAudioElRef = useRef<HTMLAudioElement | null>(null);
  const ambientSrcRef = useRef<string | null>(null);
  const ambientMutedRef = useRef(ambientMuted);

  useEffect(() => {
    ambientMutedRef.current = ambientMuted;
  }, [ambientMuted]);

  // -- Helpers ----------------------------------------------------------------

  const appendEntry = useCallback((entry: Omit<TurnEntry, "id">) => {
    const e: TurnEntry = { id: nextId(), ...entry };
    setEntries((prev) => {
      // System messages overwrite a trailing system-message instead of
      // stacking — matches frontend/app.js appendSystem().
      if (entry.kind === "system" && prev.length > 0 && prev[prev.length - 1].kind === "system") {
        const out = prev.slice(0, -1);
        return [...out, e];
      }
      return [...prev, e];
    });
    return e.id;
  }, []);

  const updateEntry = useCallback((id: number, patch: Partial<TurnEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const removeEntry = useCallback((id: number) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const getTurnAudioEl = useCallback((): HTMLAudioElement => {
    let el = turnAudioElRef.current;
    if (!el) {
      el = document.createElement("audio");
      el.id = "turn-audio";
      document.body.appendChild(el);
      turnAudioElRef.current = el;
    }
    return el;
  }, []);

  const playAudio = useCallback(
    (src: string) => {
      const el = getTurnAudioEl();
      el.src = src;
      el.play().catch(() => {});
    },
    [getTurnAudioEl],
  );

  const unlockAudio = useCallback(() => {
    const el = getTurnAudioEl();
    el.src = SILENT_MP3;
    el.play().catch(() => {});

    const amb = ambientElRef.current;
    if (amb && ambientSrcRef.current && !ambientMutedRef.current) {
      amb.src = ambientSrcRef.current;
      amb.volume = 0;
      amb.play().catch(() => {});
      fadeAudioIn(amb, 2000);
    } else if (amb) {
      amb.src = SILENT_MP3;
      amb.play().catch(() => {});
    }
  }, [getTurnAudioEl]);

  const setAmbientAudio = useCallback((url: string | null | undefined) => {
    setAmbientAudioUrl(url ?? null);
    const el = ambientElRef.current;
    if (!el) {
      // Boot-sequence race: the very first ambient src can arrive (via
      // /api/session response) before the <audio> element's ref callback
      // has run, so ``ambientElRef.current`` is still null. Stash the URL
      // on the src ref and let the audio-unlock / ref-callback path pick
      // it up once the element mounts; otherwise the first room would
      // start silent until the player walked to a second one.
      ambientSrcRef.current = url ?? null;
      return;
    }
    if (url === ambientSrcRef.current) return;
    ambientSrcRef.current = url ?? null;

    if (!url) {
      fadeAudioOut(el, 2000, () => {
        el.pause();
        el.removeAttribute("src");
      });
      return;
    }

    if (ambientMutedRef.current) return;

    if (!el.paused && el.src) {
      fadeAudioOut(el, 2000, () => {
        el.src = url;
        el.volume = 0;
        el.play().catch(() => {});
        fadeAudioIn(el, 2000);
      });
    } else {
      el.src = url;
      el.volume = 0;
      el.play().catch(() => {});
      fadeAudioIn(el, 2000);
    }
  }, []);

  const toggleAmbient = useCallback(() => {
    setAmbientMuted((prev) => {
      const next = !prev;
      localStorage.setItem("lucid_ambient_muted", String(next));
      const amb = ambientElRef.current;
      if (amb) {
        if (next) {
          fadeAudioOut(amb, 500, () => amb.pause());
        } else if (ambientSrcRef.current) {
          amb.src = ambientSrcRef.current;
          amb.volume = 0;
          amb.play().catch(() => {});
          fadeAudioIn(amb, 1000);
        }
      }
      if (next) {
        const sfx = sfxElRef.current;
        if (sfx && !sfx.paused) {
          sfx.pause();
          sfx.removeAttribute("src");
        }
      }
      return next;
    });
  }, []);

  // -- Session bootstrap ------------------------------------------------------

  const renderSessionMeta = useCallback(
    (data: SessionResponse | TurnDoneMeta & { world_title?: string }) => {
      if ("world_title" in data && data.world_title) setWorldTitle(data.world_title);
      if ("location_name" in data && data.location_name) {
        setLocationName(data.location_name);
        const wt = (data as { world_title?: string }).world_title || worldTitle;
        document.title = `${data.location_name} — ${wt}`;
      }
      if ("ambient_image_url" in data) setAmbientImageUrl(data.ambient_image_url ?? null);
      if ("ambient_audio_url" in data) {
        setAmbientAudio(data.ambient_audio_url);
      }
    },
    [setAmbientAudio, worldTitle],
  );

  const loadOrCreateSession = useCallback(async () => {
    // Issue #535: if the player has a remembered world (URL or
    // localStorage) hit the per-world endpoints; if not, send them to
    // the picker.
    const worldName = worldNameRef.current;
    if (!worldName) {
      navigate("/worlds", { replace: true });
      return;
    }

    try {
      // GET first (returns the existing per-(user, world) session if
      // there is one), fall back to POST to get-or-create.
      let data: SessionByWorldResponse;
      try {
        data = await api<SessionByWorldResponse>(
          `/api/sessions/by-world/${encodeURIComponent(worldName)}`,
        );
      } catch (e) {
        const err = e as ApiError;
        if (err.status !== 404) throw e;
        data = await api<SessionByWorldResponse>(
          "/api/sessions",
          "POST",
          { world_name: worldName },
        );
      }
      try {
        localStorage.setItem(LAST_WORLD_STORAGE_KEY, worldName);
      } catch {
        /* private-mode etc — non-fatal */
      }
      if (data.session_id) setSessionId(data.session_id);

      let renderedFromHistory = false;
      let exitsFromGet: SessionResponse["available_exits"] | undefined;
      try {
        const full = await api<SessionResponse>(
          `/api/session?world_name=${encodeURIComponent(worldName)}`,
        );
        if (full.history && full.history.length > 0) {
          for (const entry of full.history) {
            if (entry.narration) {
              appendEntry({
                kind: "turn",
                action: entry.action,
                narration: stripBoundaryWhitespace(entry.narration),
              });
            } else {
              appendEntry({ kind: "turn", action: entry.action });
            }
          }
          renderedFromHistory = true;
        }
        // Issue #444: the GET reflects the post-restart / mid-session
        // state — prefer its exits over the POST response.
        exitsFromGet = full.available_exits;
      } catch {
        /* best-effort */
      }

      if (!renderedFromHistory) {
        appendEntry({ kind: "narration", narration: stripBoundaryWhitespace(data.narration) });
      }
      setTurn(data.turn);
      setStatus("");
      if (data.world_title) {
        setWorldTitle(data.world_title);
        document.title = data.world_title;
      }
      // Issue #535: SessionByWorldResponse doesn't carry
      // ``location_name`` — it arrives on the next SSE ``done``
      // payload (turn boundary). The header will pick it up then.
      if (data.ambient_image_url !== undefined) setAmbientImageUrl(data.ambient_image_url ?? null);
      if (data.ambient_audio_url !== undefined) setAmbientAudio(data.ambient_audio_url);
      // Issue #444: prefer the /api/session GET response's exits since
      // it reflects post-restart / mid-session state; fall back to the
      // create-session POST response on fresh accounts where the GET
      // history fetch is empty or failed. The SSE ``done`` handler
      // keeps the strip fresh after every turn.
      setAvailableExits(exitsFromGet ?? data.available_exits ?? []);
      // Issue #493: seed the present-NPC list for the do/say toggle's
      // "say to" dropdown.
      setPresentNpcs(data.present_npcs ?? []);
      // Issue #545: seed the takeable-items list for the "Take"
      // quick-action chip's drop-up popover.
      setTakeableEntities(data.takeable_entities ?? []);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) {
        // TODO(#464): wire Clerk sign-in flow; this UI has no auth gate yet.
        appendEntry({
          kind: "system",
          text:
            "Authenticated session required. This UI is currently dev-bypass only — " +
            "run the engine with LUCID_AUTH_DEV_BYPASS=1, or wait for the Clerk " +
            "integration follow-up.",
        });
        return;
      }
      appendEntry({ kind: "error", text: `Failed to load game: ${err.message}` });
    }
  }, [appendEntry, setAmbientAudio]);

  const restart = useCallback(async () => {
    // Issue #588: flip the restarting flag immediately so the header
    // button can disable + spin while the ~10s bootstrap narration is
    // in flight.
    setRestarting(true);
    try {
      // Issue #535: restart targets the world this provider was
      // booted into. Without ``world_name`` the backend returns 422.
      const worldQs = worldNameRef.current
        ? `?world_name=${encodeURIComponent(worldNameRef.current)}`
        : "";
      const data = await api<SessionResponse>(
        `/api/session/restart${worldQs}`,
        "POST",
      );
      if (data.session_id) setSessionId(data.session_id);
      setEntries([]);
      appendEntry({ kind: "narration", narration: stripBoundaryWhitespace(data.narration) });
      setTurn(data.turn);
      setStatus("");
      if (data.world_title) setWorldTitle(data.world_title);
      if (data.location_name) {
        setLocationName(data.location_name);
        document.title = `${data.location_name} — ${data.world_title || "Lucid"}`;
      }
      if (data.ambient_image_url !== undefined) setAmbientImageUrl(data.ambient_image_url ?? null);
      if (data.ambient_audio_url !== undefined) setAmbientAudio(data.ambient_audio_url);
      // Issue #444: restart re-seeds at the start location, so the
      // quick-actions strip needs to mirror the freshly-seeded exits.
      setAvailableExits(data.available_exits ?? []);
      // Issue #493: re-seed present-NPC list on restart.
      setPresentNpcs(data.present_npcs ?? []);
      // Issue #545: re-seed takeable-items list on restart.
      setTakeableEntities(data.takeable_entities ?? []);
    } catch (e) {
      const err = e as ApiError;
      appendEntry({ kind: "error", text: `Failed to restart: ${err.message}` });
    } finally {
      setRestarting(false);
    }
  }, [appendEntry, setAmbientAudio]);

  // -- Streaming turn ---------------------------------------------------------

  type StreamResult = "ok" | "stale" | "session_missing" | "unavailable";

  const submitActionStream = useCallback(
    async (
      action: string,
      opts: {
        retry?: boolean;
        quickAction?: QuickAction;
        intent?: Intent;
        targetEntityId?: string;
      } = {},
    ): Promise<StreamResult> => {
      const { retry = false, quickAction, intent, targetEntityId } = opts;

      // Mount the streaming turn entry IMMEDIATELY on submit so the
      // player's action line + blinking caret render without a gap.
      // Chunks will append to ``narration`` as they arrive (#540).
      const turnEntryId = appendEntry({
        kind: "turn",
        action,
        narration: "",
        streaming: true,
      });

      // SSE pump uses fetch + ReadableStream so it can carry custom
      // headers (issue #464 — EventSource doesn't support Authorization).
      // Issue #499: ``quick_action`` carries a typed UI intent (e.g.
      // a chip click). When present the server skips classifier +
      // router and dispatches straight to the named facet.
      // Issue #493: ``intent`` + ``target_entity_id`` carry the do/say
      // toggle's state. When ``intent === "say"`` the server skips
      // router and dispatches straight to the character facet.
      const body: Record<string, unknown> = { action };
      if (quickAction) body.quick_action = quickAction;
      if (intent) body.intent = intent;
      if (targetEntityId) body.target_entity_id = targetEntityId;
      // Issue #535: backend requires ``world_name`` on every turn so
      // the request routes to the world this session belongs to.
      if (worldNameRef.current) body.world_name = worldNameRef.current;
      const resp = await fetch(`${apiBase()}/api/turn/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeadersOnly()) },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (resp.status === 503) {
        // Drop the placeholder so the retry path can mount a fresh
        // streaming entry rather than render duplicate action labels.
        removeEntry(turnEntryId);
        return "unavailable";
      }
      if (!resp.ok) {
        removeEntry(turnEntryId);
        let detail = resp.statusText;
        try {
          const err = await resp.json();
          if (err && typeof err === "object" && "detail" in err) {
            detail = (err as { detail?: string }).detail || detail;
          }
        } catch {
          /* ignore */
        }
        const e = new ApiError(detail || `HTTP ${resp.status}`, resp.status);
        throw e;
      }

      const audioChunks: Uint8Array[] = [];
      let meta: { cached?: boolean; cached_audio_url?: string } | null = null;
      let done: TurnDoneMeta | null = null;
      let errorDetail: string | null = null;
      let errorCode: string | null = null;
      let streamed = "";

      const reader = resp.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done: readerDone, value } = await reader.read();
        if (readerDone) break;
        buffer += decoder.decode(value, { stream: true });

        let sep: number;
        while ((sep = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const evt = parseSseEvent(raw);
          if (!evt) continue;

          if (evt.type === "meta") {
            meta = JSON.parse(evt.data);
          } else if (evt.type === "narration") {
            streamed += evt.data;
            updateEntry(turnEntryId, { narration: streamed });
          } else if (evt.type === "audio") {
            audioChunks.push(b64ToBytes(evt.data));
          } else if (evt.type === "done") {
            done = JSON.parse(evt.data);
          } else if (evt.type === "error") {
            const payload = JSON.parse(evt.data);
            errorDetail = payload.error;
            errorCode = payload.code || null;
          }
        }
      }

      // Stop the streaming caret regardless of outcome. Issue #541:
      // commit the trimmed narration on stream completion so any
      // boundary "\n\n" the model emitted doesn't render as a blank
      // line under `white-space: pre-wrap`. We don't trim mid-stream
      // (the in-progress text reads correctly with newlines intact).
      updateEntry(turnEntryId, {
        narration: stripBoundaryWhitespace(streamed),
        streaming: false,
      });

      if (errorDetail) {
        if (errorCode === "stale_state" && !retry) {
          // Wipe the partially-streamed turn so the retry can render
          // a clean turn without doubled prose / action labels.
          removeEntry(turnEntryId);
          return "stale";
        }
        if (errorCode === "session_missing") {
          removeEntry(turnEntryId);
          appendEntry({ kind: "error", text: `Error: ${errorDetail}` });
          return "session_missing";
        }
        appendEntry({ kind: "error", text: `Error: ${errorDetail}` });
        return "ok";
      }

      if (meta && meta.cached) {
        updateEntry(turnEntryId, { cached: true });
      }

      // Audio playback — presigned URL on full cache hit, otherwise the
      // streamed chunks assembled into a Blob.
      if (meta && meta.cached_audio_url) {
        playAudio(meta.cached_audio_url);
      } else if (audioChunks.length > 0) {
        const blob = new Blob(audioChunks as BlobPart[], { type: "audio/mpeg" });
        playAudio(URL.createObjectURL(blob));
      }

      if (done) {
        const cached = meta && meta.cached ? "(cached)" : "";
        const investigation = done.is_investigation ? "(investigation)" : "";
        setStatus([cached, investigation].filter(Boolean).join(" "));
        setTurn(done.new_turn);
        if (done.location_name) {
          setLocationName(done.location_name);
          document.title = `${done.location_name} — ${worldTitle || "Lucid"}`;
        }
        if (done.ambient_image_url !== undefined) setAmbientImageUrl(done.ambient_image_url ?? null);
        if (done.ambient_audio_url !== undefined) setAmbientAudio(done.ambient_audio_url);
        if (done.sfx_url && !ambientMutedRef.current) playSfx(sfxElRef.current, done.sfx_url);
        // Issue #444: keep the quick-actions strip in sync with the
        // post-turn location.
        if (done.available_exits !== undefined) {
          setAvailableExits(done.available_exits ?? []);
        }
        // Issue #493: keep the present-NPC list fresh for the do/say
        // toggle's "say to" dropdown.
        if (done.present_npcs !== undefined) {
          setPresentNpcs(done.present_npcs ?? []);
        }
        // Issue #545: keep the takeable-items list fresh for the "Take"
        // quick-action chip's drop-up popover.
        if (done.takeable_entities !== undefined) {
          setTakeableEntities(done.takeable_entities ?? []);
        }
        // Issue #641: render any typed engine→player non-diegetic
        // messages as visually distinct system entries. v1 displays
        // body text only — choice click-handling lands with the
        // disambiguation work that depends on cross-turn pending state.
        if (done.system_messages && done.system_messages.length > 0) {
          for (const msg of done.system_messages) {
            appendEntry({ kind: "system", text: msg.body });
          }
        }
      }

      return "ok";
    },
    [appendEntry, playAudio, removeEntry, setAmbientAudio, updateEntry, worldTitle],
  );

  const submitActionStreamWithRetry = useCallback(
    async (
      action: string,
      opts: {
        quickAction?: QuickAction;
        intent?: Intent;
        targetEntityId?: string;
      } = {},
    ): Promise<StreamResult> => {
      try {
        const result = await submitActionStream(action, opts);
        if (result === "stale") {
          await new Promise((r) => setTimeout(r, 250));
          return await submitActionStream(action, { ...opts, retry: true });
        }
        return result;
      } catch (e) {
        const err = e as ApiError;
        if (err.status === 409) {
          await new Promise((r) => setTimeout(r, 250));
          return await submitActionStream(action, { ...opts, retry: true });
        }
        if (err.status === 410) {
          appendEntry({ kind: "error", text: `Error: ${err.message}` });
          return "session_missing";
        }
        throw e;
      }
    },
    [appendEntry, submitActionStream],
  );

  const submitCommand = useCallback(
    async (command: string) => {
      try {
        // Issue #535: world_name is a required query param on /api/command.
        const worldQs = worldNameRef.current
          ? `?world_name=${encodeURIComponent(worldNameRef.current)}`
          : "";
        const data = await api<{ message?: string; narration?: string }>(
          `/api/command${worldQs}`,
          "POST",
          { command },
        );
        if (data.message) appendEntry({ kind: "system", text: data.message });
        if (data.narration)
          appendEntry({ kind: "narration", narration: stripBoundaryWhitespace(data.narration) });
      } catch (e) {
        const err = e as ApiError;
        appendEntry({ kind: "error", text: `Error: ${err.message}` });
      }
    },
    [appendEntry],
  );

  const submitAction = useCallback(
    async (
      action: string,
      opts: {
        quickAction?: QuickAction;
        intent?: Intent;
        targetEntityId?: string;
      } = {},
    ) => {
      if (busy || !action.trim()) return;

      if (action.startsWith("/")) {
        setBusy(true);
        await submitCommand(action);
        setBusy(false);
        return;
      }

      setBusy(true);
      // Unlock audio inside the user-gesture stack BEFORE any await.
      unlockAudio();
      try {
        await submitActionStreamWithRetry(action, {
          quickAction: opts.quickAction,
          intent: opts.intent,
          targetEntityId: opts.targetEntityId,
        });
      } catch (e) {
        const err = e as ApiError;
        appendEntry({ kind: "error", text: `Error: ${err.message}` });
      }
      setBusy(false);
    },
    [appendEntry, busy, submitActionStreamWithRetry, submitCommand, unlockAudio],
  );

  // -- Boot -------------------------------------------------------------------

  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    void loadOrCreateSession();
  }, [loadOrCreateSession]);

  // First-gesture audio unlock — matches frontend/app.js
  // _armAudioUnlockOnFirstGesture.
  useEffect(() => {
    const events: (keyof DocumentEventMap)[] = ["pointerdown", "keydown", "touchstart"];
    const handler = () => {
      events.forEach((e) => document.removeEventListener(e, handler, true));
      unlockAudio();
    };
    events.forEach((e) => document.addEventListener(e, handler, true));
    return () => events.forEach((e) => document.removeEventListener(e, handler, true));
  }, [unlockAudio]);

  // Element registration callbacks — refs come from <audio> tags
  // mounted by Play.tsx.
  const registerAmbientEl = useCallback((el: HTMLAudioElement | null) => {
    ambientElRef.current = el;
  }, []);
  const registerSfxEl = useCallback((el: HTMLAudioElement | null) => {
    sfxElRef.current = el;
  }, []);
  const registerTurnAudioEl = useCallback((el: HTMLAudioElement | null) => {
    turnAudioElRef.current = el;
  }, []);

  const value: GameApi = {
    busy,
    restarting,
    worldTitle,
    locationName,
    turn,
    sessionId,
    status,
    ambientImageUrl,
    ambientAudioUrl,
    ambientMuted,
    entries,
    availableExits,
    presentNpcs,
    takeableEntities,
    submitAction,
    restart,
    toggleAmbient,
    unlockAudio,
    registerAmbientEl,
    registerSfxEl,
    registerTurnAudioEl,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

// Used by HistoryEntry-shaped helpers; kept for API parity if needed.
export type { HistoryEntry };
